import React, { useRef, useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { ChartSchema, ChartTooltipData, ChartType } from '../../types/types';
import { AxesRenderer } from './renderers/axesRenderer';
import { getChartRenderer } from './renderers';
import { createXScale, createYScale, generateYTicks, calculateYMinMax, getUniqueXValues, ScaleFunction } from './utils';
import { formatYValue } from './utils/formatting';
import { ChartTooltip } from './utils/tooltip';
import { findClosestXValue } from './utils';
import { assignDefaultColors } from './utils/colors';
import './SimpleChart.scss';
import { ChartDataPoint, ChartSeries } from '../../types/types';

interface SimpleChartProps {
  data: ChartSchema;
  className?: string;
  title?: string;
  subtitle?: string;
  reversed?: boolean;
  overrideData?: ChartDataPoint[];
}

const SimpleChart: React.FC<SimpleChartProps> = ({ data, className, title, subtitle, reversed, overrideData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<ChartTooltipData | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 300 });

  // Apply default colors to series that don't have colors defined
  const processedData = useMemo(() => {
    // Start with the original series data or empty array
    let seriesData = data.series || [];

    // Check if we have overrideData to use
    if (overrideData && overrideData.length > 0) {
      // We need to check if the overrideData is an array of ChartSeries or ChartDataPoint
      // Since we're getting this from aiAnalysis.data.aiChartMetrics, we'll assume it's ChartSeries
      // but we'll do a type check to be safe

      // Check if the first item has an 'id' and 'data' property (indicating it's a ChartSeries)
      const firstItem = overrideData[0] as any;

      if (firstItem && typeof firstItem === 'object' && 'id' in firstItem && 'data' in firstItem) {
        // It's already in ChartSeries format
        seriesData = overrideData as unknown as ChartSeries[];

        // Add default fillOpacity if needed
        seriesData = seriesData.map(series => ({
          ...series,
          fillOpacity: typeof series.fillOpacity === 'number' ? series.fillOpacity : 0.3,
        }));
      } else {
        // It's likely an array of ChartDataPoint, so we need to adapt it
        // We'll use the existing series structure but replace the data
        if (seriesData.length > 0) {
          seriesData = seriesData.map(series => ({
            ...series,
            data: overrideData as ChartDataPoint[],
          }));
        } else {
          // If we don't have any existing series, create a new one
          seriesData = [
            {
              id: 'default',
              name: 'Data',
              data: overrideData as ChartDataPoint[],
              fillOpacity: 0.3,
            },
          ];
        }
      }
    }

    return {
      ...data,
      series: assignDefaultColors(seriesData),
      // Set default reverse to true if not specified
      reverse: data.reverse !== undefined ? data.reverse : reversed !== undefined ? reversed : true,
    };
  }, [data, overrideData, reversed]);

  // Use layout effect to measure container and update dimensions
  useLayoutEffect(() => {
    if (containerRef.current) {
      const updateDimensions = () => {
        const containerWidth = containerRef.current?.clientWidth || 0;
        setDimensions({
          width: containerWidth,
          height: processedData.height || 300,
        });
      };

      // Initial measurement
      updateDimensions();

      // Set up resize observer
      const resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(containerRef.current);

      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
        resizeObserver.disconnect();
      };
    }
  }, [processedData.height, reversed]);

  // Use actual dimensions or fallback to defaults
  const width = dimensions.width || processedData.width || 600;
  const height = dimensions.height;

  // Chart margins
  const margin = { top: 20, right: 20, bottom: 30, left: 50 };

  // Calculate chart area dimensions
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Chart type from data or default to line
  const chartType = processedData.type || 'line';

  // Check if we're using a horizontal bar chart
  const isHorizontalBarChart = chartType === 'horizontal-bar';

  // Calculate the y-axis min and max values
  const yMinMax = useMemo(() => {
    // For horizontal bar charts, we need to calculate the x-axis min/max for values
    return calculateYMinMax(processedData.series, chartType);
  }, [processedData.series, chartType]);

  // Get unique x values for the x-axis (using indexes)
  const xValues = useMemo(() => {
    return getUniqueXValues(processedData.series, processedData.reverse) || [];
  }, [processedData.series, processedData.reverse, reversed]);

  // Get original labels for display
  const xLabels = useMemo(() => {
    if (!processedData.series[0]?.data) return [];
    const labels = processedData.series[0].data.map(point => point.x);
    return processedData.reverse ? labels.reverse() : labels;
  }, [processedData.series, processedData.reverse]);

  // For horizontal bar charts, we need the number of categories for the y-axis
  const categoryCount = xValues.length;

  // Create scale functions
  const xScaleFn: ScaleFunction = useMemo(() => {
    // For horizontal bar charts, the x-axis represents values
    if (isHorizontalBarChart) {
      return createYScale(yMinMax, width, chartWidth, margin);
    }
    // For regular charts, the x-axis represents categories
    return createXScale(xValues, chartWidth, margin);
  }, [isHorizontalBarChart, xValues, yMinMax, chartWidth, width, margin]);

  const yScaleFn: ScaleFunction = useMemo(() => {
    // For horizontal bar charts, the y-axis represents categories
    if (isHorizontalBarChart) {
      // Create a scale for categories on the y-axis
      const categoryScale: ScaleFunction = (value: string | number) => {
        // Convert string values to their index in the xValues array
        const numericValue = typeof value === 'string' ? xValues.indexOf(value) : value;
        // Distribute categories evenly along the y-axis
        const step = chartHeight / categoryCount;
        return margin.top + numericValue * step + step / 2;
      };
      return categoryScale;
    }
    // For regular charts, the y-axis represents values
    return createYScale(yMinMax, height, chartHeight, margin);
  }, [isHorizontalBarChart, yMinMax, height, chartHeight, margin, categoryCount, xValues]);

  // Generate Y-axis ticks
  const yTicks = useMemo(() => generateYTicks(yMinMax), [yMinMax]);

  // Format Y axis values
  const formatYValueFn = (value: string | number): string => {
    // Ensure value is a number before formatting
    const numValue = typeof value === 'string' ? Number(value) : value;
    return formatYValue(numValue, processedData.yAxisPrefix, processedData.yAxisSuffix);
  };

  // Get the appropriate renderer based on chart type
  const ChartRenderer = getChartRenderer(chartType);

  // Handle mouse move for tooltips
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left;

    // Create array of position indices for finding closest value
    const positionIndices = Array.from({ length: xLabels.length }, (_, i) => i.toString());

    // Find closest position index
    const closestPositionIndex = findClosestXValue(mouseX, positionIndices, xScaleFn);
    const xPos = xScaleFn(closestPositionIndex);

    // Get the actual position index number
    const positionIndex = parseInt(closestPositionIndex.toString());

    // When reversed, we need to map the position index to the correct data index
    const dataIndex = processedData.reverse ? xLabels.length - 1 - positionIndex : positionIndex;

    // Get the original label for this position
    const originalLabel = xLabels[positionIndex] || closestPositionIndex;

    // Format value function for tooltip
    const formatValueForTooltip = (value: string | number) => formatYValueFn(value);

    // Create tooltip data
    const tooltipData = {
      x: closestPositionIndex,
      xLabel: String(originalLabel),
      series: processedData.series.map(series => {
        // Get the data point at the correct data index
        const point = series.data[dataIndex];
        return {
          id: series.id,
          name: series.name,
          value: point?.y || 0,
          color: series.color || '#666666',
          formattedValue: point ? formatValueForTooltip(point.y) : '0',
        };
      }),
      position: {
        x: xPos,
        y: 0, // Will be calculated below
      },
    };

    // Calculate y position for tooltip (top of highest point)
    tooltipData.position.y = Math.min(
      ...tooltipData.series.map(s => {
        return typeof s.value === 'string' ? yScaleFn(Number(s.value)) : yScaleFn(s.value);
      })
    );

    setTooltipData(tooltipData);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setTooltipData(null);
  };

  return (
    <div className={`simple-chart ${className || ''}`} ref={containerRef}>
      {(title || processedData.title) && <div className="simple-chart-title">{title || processedData.title}</div>}
      {(subtitle || processedData.subtitle) && (
        <div className="simple-chart-subtitle">{subtitle || processedData.subtitle}</div>
      )}
      <div className="simple-chart-container">
        <svg ref={svgRef} width={width} height={height} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
          {/* Render axes and grid */}
          <AxesRenderer
            width={width}
            height={height}
            margin={margin}
            xValues={xLabels}
            yTicks={yTicks}
            xScale={xScaleFn}
            yScale={yScaleFn}
            yAxisPrefix={processedData.yAxisPrefix}
            yAxisSuffix={processedData.yAxisSuffix}
            chartType={chartType}
            reversed={processedData.reverse}
          />

          {/* Render threshold lines for outlier charts if needed */}
          {processedData.showThresholds &&
            processedData.thresholds &&
            processedData.thresholds.map((threshold, i) => (
              <line
                key={`threshold-${i}`}
                x1={margin.left}
                y1={yScaleFn(threshold.value)}
                x2={width - margin.right}
                y2={yScaleFn(threshold.value)}
                stroke={threshold.color || '#ff6b6b'}
                strokeWidth={1.5}
                strokeDasharray="5 3"
              />
            ))}

          {/* Render the appropriate chart type */}
          <ChartRenderer
            series={
              processedData.series?.map(series => ({
                ...series,
                data: series.data.map((point, index) => ({
                  ...point,
                  x: index.toString(), // Use index as x value for positioning
                })),
              })) || []
            }
            xScale={xScaleFn}
            yScale={yScaleFn}
            yMinMax={yMinMax}
            chartType={chartType}
          />

          {/* Tooltip vertical line */}
          {tooltipData && (
            <line
              x1={tooltipData.position.x}
              y1={margin.top}
              x2={tooltipData.position.x}
              y2={height - margin.bottom}
              stroke="#999"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
          )}
        </svg>
        {/* Tooltip */}
        {tooltipData && <ChartTooltip data={tooltipData} />}
      </div>
      {/* Chart legend */}
      {processedData.showLegend && (
        <div className="simple-chart-legend">
          {processedData.series.map(series => (
            <div key={`legend-${series.id}`} className="simple-chart-legend-item">
              <div className="simple-chart-legend-color" style={{ backgroundColor: series.color }} />
              <div className="simple-chart-legend-label">{series.name}</div>
            </div>
          ))}
        </div>
      )}
      {processedData.description && <div className="simple-chart-description">{processedData.description}</div>}
    </div>
  );
};

export default SimpleChart;
